import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { BUSINESS } from "../data/content";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 border-b ${
          scrolled
            ? "py-2.5 bg-[#0B0C1A]/90 backdrop-blur-xl border-[rgba(255,255,255,0.07)]"
            : "py-5 bg-transparent border-transparent"
        }`}
      >
        <div className="container-x flex items-center justify-between">
          <Logo withTagline={!scrolled} />
          <nav className="hidden lg:flex items-center gap-7" aria-label="Main">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[0.92rem] font-medium no-underline transition-colors ${
                    isActive ? "text-[#00D9C0]" : "text-[#A0A3C2] hover:text-[#F5F5FA]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={BUSINESS.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-btn"
              className="btn btn-wa !py-2.5 !px-5 text-sm no-underline"
            >
              <MessageCircle size={17} /> Chat on WhatsApp
            </a>
          </nav>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(true)}
            className="lg:hidden p-2.5 text-[#F5F5FA] bg-transparent border border-[rgba(255,255,255,0.15)] rounded-lg cursor-pointer"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 bg-[#0B0C1A]/98 backdrop-blur-lg flex flex-col"
          >
            <div className="container-x flex items-center justify-between py-5">
              <Logo />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="p-2.5 text-[#F5F5FA] bg-transparent border border-[rgba(255,255,255,0.15)] rounded-lg cursor-pointer"
                aria-label="Close menu"
              >
                <X size={22} />
              </button>
            </div>
            <nav className="container-x flex flex-col gap-1 mt-6 flex-1" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block py-3 font-heading font-semibold text-3xl no-underline tracking-tight ${
                        isActive ? "text-[#00D9C0]" : "text-[#F5F5FA]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55 }}
                className="mt-auto pb-10 flex flex-col gap-3"
              >
                <a href={BUSINESS.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="mobile-whatsapp-btn" className="btn btn-wa no-underline w-full">
                  <MessageCircle size={18} /> Chat on WhatsApp
                </a>
                <a href={BUSINESS.phoneHref} data-testid="mobile-call-btn" className="btn btn-ghost no-underline w-full">
                  Call {BUSINESS.phoneDisplay}
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
